<template>
    <section v-if="product.options.length" class="app-product-options-view container-fluid">
        <div class="app-view-header d-flex flex-wrap justify-content-center">
            <div class="app-view-header__headline-wrap d-flex align-items-center">
                <span class="app-view-header__headline-wrap-pseudo-helper"></span>
                <h2 class="app-view-header__headline">{{ $t('productOptions.headline') }}</h2>
                <span class="app-view-header__headline-wrap-pseudo-helper"></span>
            </div>
        </div>
        <div class="app-product-options-view__main">
            <section v-if="insuranceOptions.length" class="app-product-options-view__option-group row">
                <div class="app-product-options-view__option-group-header col-12 col-lg-3 mb-lg-0 word-wrap-break">
                    <h4 class="app-product-options-view__option-group-headline" v-html="$t('productOptions.insurance')" />
                </div>
                <div class="app-product-options-view__option-group-content col">
                    <product-option v-for="(option, idx) in insuranceOptions"
                        :key="idx"
                        :headline="option.name"
                        :previewText="option.description"
                        :price="option.price"
                        :inclusive="option.inclusive"
                        :value="isChecked(option.uid)"
                        @click.native="toggleOption(option.uid)"
                    />
                </div>
            </section>
            <section v-if="attachmentOptions.length" class="app-product-options-view__option-group row">
                <div class="app-product-options-view__option-group-header col-12 col-lg-3 mb-lg-0 word-wrap-break">
                    <h4 class="app-product-options-view__option-group-headline" v-html="$tc('product.attachment', 2)" />
                    <span v-if="tExists('productOptions.attachmentOptionsInfo')" class="app-product-options-view__option-group-info d-block" v-t="{ path: 'productOptions.attachmentOptionsInfo' }"></span>
                </div>
                <div class="app-product-options-view__option-group-content col">
                    <product-option v-for="(option, idx) in attachmentOptions"
                        :key="idx"
                        :headline="option.name"
                        :previewText="option.description"
                        :price="option.price"
                        :inclusive="option.inclusive"
                        :value="isChecked(option.uid)"
                        @click.native="toggleOption(option.uid)"
                    />
                </div>
            </section>
            <section v-if="batteryOptions.length" class="app-product-options-view__option-group row">
                <div class="app-product-options-view__option-group-header col-12 col-lg-3 mb-lg-0 word-wrap-break">
                    <h4 class="app-product-options-view__option-group-headline" v-html="$tc('productOptions.battery')" />
                    <span v-if="tExists('productOptions.batteriesInfo')" class="app-product-options-view__option-group-info d-block" v-t="{ path: 'productOptions.batteriesInfo' }"></span>
                </div>
                <div class="app-product-options-view__option-group-content col">
                    <template v-for="(option, idx) in batteryOptions">
                        <product-option v-if="option.isLiIon"
                            :key="idx"
                            :headline="option.name"
                            :headlineNote="tExists('productOptions.featuredOptionClaim') ? $t('productOptions.featuredOptionClaim') : ''"
                            bgImgUrl="li-ion_bg_img.jpg"
                            designType="2"
                            :price="option.price"
                            :inclusive="option.inclusive"
                            :value="isChecked(option.uid)"
                            @click.native="toggleOption(option.uid)"
                        />
                        <product-option v-else
                            :key="idx"
                            :headline="option.name"
                            :previewText="option.description"
                            :price="option.price"
                            :inclusive="option.inclusive"
                            :value="isChecked(option.uid)"
                            @click.native="toggleOption(option.uid)"
                        />
                    </template>
                </div>
            </section>
        </div>
        <div class="app-view-footer app-product-options-view__footer">
            <div class="app-view-footer__inner row flex-column flex-md-row justify-content-md-around align-items-center align-items-md-start">
                <div class="app-view-footer__col col">
                    <div class="app-view-footer__btn-wrap">
                        <button type="button" class="app-view-footer__btn btn btn--primary" :class="{ 'app-view-footer__btn--hidden': currentStep > 2 }" @click="goForward">{{ $t('productOptions.goForward') }} &downarrow;</button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import ProductOption from '@/components/ProductOption.vue'

export default {
    name: 'ProductOptions',
    components: {
        ProductOption
    },
    computed: {
        product: (vm) => vm.$store.getters.productMapped,
        productPathSegment: (vm) => vm.$store.state.productPathSegment,
        insuranceOptions: (vm) => {
            return vm.product.options.filter(option => /^SMP/.test(option.uid))
                .map((option) => Object.assign({}, option, {
                    price: vm.pricesOfProductOptions[option.uid],
                    description: (() => {
                        let description = option.description
                        if (vm.product.excessBurden && vm.tExists('productOptions.excessBurden')) {
                            description += '<br>' + vm.$t('productOptions.excessBurden', { excessBurden: vm.nEnhanced(vm.product.excessBurden, 'currency') })
                        }
                        return description
                    })()
                }))
        },
        attachmentOptions: (vm) => {
            return vm.product.options.filter(option => /^[AGF]\d+/.test(option.uid))
                .map((option) => Object.assign({}, option, {
                    price: vm.pricesOfProductOptions[option.uid]
                }))
        },
        batteryOptions: (vm) => {
            return vm.product.options.filter(option => /^B\d+/.test(option.uid))
                .map((option) => Object.assign({}, option, {
                    price: vm.pricesOfProductOptions[option.uid]
                }))
                .sort((optionA, optionB) => {
                    if (optionA.isLiIon === true) return 1
                    else if (optionB.isLiIon === true) return -1
                    return 0
                })
        },
        checkedOptions: (vm) => vm.$store.state.uidsOfSelectedProductOptions,
        currentStep: (vm) => vm.$store.state.currentStep,
        pricesOfProductOptions: (vm) => vm.$store.state.pricesOfProductOptions
    },
    methods: {
        isChecked (id) {
            return this.checkedOptions.indexOf(id) !== -1
        },
        toggleOption (id) {
            const newOptionUids = this.checkedOptions.slice()
            if (this.isChecked(id)) {
                newOptionUids.splice(this.checkedOptions.indexOf(id), 1)
            } else {
                newOptionUids.push(id)
            }
            this.$store.commit('replaceUidsOfSelectedProducts', newOptionUids)
        },
        goForward (replace) {
            if (this.currentStep < 3) {
                this.$store.commit('setCurrentStep', 3)
            }

            this.$router[replace === true ? 'replace' : 'push']({
                name: 'steps',
                hash: '#personal-data',
                params: {
                    productPathSegment: this.productPathSegment
                }
            })
        }
    },
    watch: {
        $route () {
            if (this.$route.params && this.$route.hash === '#product-options' && !this.product.options.length) {
                this.goForward(true)
            }
        }
    },
    created () {
        if (!this.product.options.length) this.goForward(true)
    }
}
</script>

<style scoped>
    .app-product-options-view {
        position: relative;
        max-width: 1200px;
        padding-bottom: 20px;
    }
    .app-product-options-view__main {
        margin-bottom: 2em;
    }
    .app-product-options-view__option-group {
        margin-bottom: 2em;
    }
    .app-product-options-view__option-group-header {
        position: relative;
        margin-bottom: 1em;
        padding-top: 2em;
    }
    .app-product-options-view__option-group-header:before {
        content: '';
        position: absolute;
        left: 15px;
        top: 0;
        right: 15px;
        height: 2px;
        background-color: #cbc9c8;
    }
    .app-product-options-view__option-group-headline {
        margin-bottom: .8em;
    }
    .app-product-options-view__option-group-headline:last-child {
        margin-bottom: 0;
    }
    .app-product-options-view__option-group-info {
        line-height: 1.8;
        color: #969492;
    }

    /* modularization intended */
    .app-product-options-view__option-group-content .app-product-option {
        margin-bottom: 2px;
        cursor: pointer;
    }
    .app-product-options-view__option-group-content .app-product-option:last-child {
        margin-bottom: 0;
    }
</style>
